import React, { useMemo, useState, memo, useEffect, useRef } from 'react';
import { resolveImageUrl, getBackendBaseUrl } from '../utils/urlResolver';

const isDev = process.env.NODE_ENV === 'development';

// Function to return analysis text as-is without any formatting
const enhanceAnalysisText = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  // Return text without any modifications
  return text;
};

// Completely static artwork that never changes
const StaticArtwork = memo(({ imageUrl, title }) => {
  const containerRef = useRef(null);
  const isSetRef = useRef(false);

  useEffect(() => {
    if (!imageUrl || !containerRef.current || isSetRef.current) {
      return;
    }
    
    if (isDev) console.log('[StaticArtwork] Setting artwork once');
    const resolvedUrl = resolveImageUrl(imageUrl);
    const img = document.createElement('img');
    img.src = resolvedUrl;
    img.alt = title || 'Artwork';
    img.onerror = function onImgError() {
      const current = this.src || imageUrl;
      if (current && !current.includes('/proxy-image?') && /^https?:\/\//i.test(current)) {
        try {
          const proxyUrl = `${getBackendBaseUrl()}/proxy-image?url=${encodeURIComponent(current)}`;
          this.onerror = null;
          this.src = proxyUrl;
        } catch (_) {}
      }
    };
    img.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: contain;
      object-position: center;
      background-color: white;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
      pointer-events: none;
      will-change: auto;
      backface-visibility: hidden;
      transform: translateZ(0);
      image-rendering: auto;
      -webkit-image-rendering: auto;
      opacity: 1;
      transition: none !important;
      filter: none;
      animation: none;
      -webkit-animation: none;
      contain: layout style paint;
      isolation: isolate;
    `;
    
    // Add image to container
    containerRef.current.appendChild(img);
    
    // Mark as set to prevent any future changes
    isSetRef.current = true;
    
    if (isDev) console.log('[StaticArtwork] Artwork set once');
  }, [imageUrl, title]);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none',
        willChange: 'auto',
        backfaceVisibility: 'hidden',
        transform: 'translateZ(0)',
        contain: 'layout style paint',
        isolation: 'isolate'
      }}
    />
  );
}, () => true); // Never re-render

// Interactive overlay component that handles all state changes
const InteractiveOverlay = memo(({ points, activeIndex, onHotspotClick, layout, showHeader = true, hideHotspots = false }) => {
  if (isDev) console.log('[InteractiveOverlay] render', { activeIndex, pointsLen: points.length });
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Calculate header height to position buttons below it (header is ~90px tall on desktop)
  // Increased margin to ensure no overlap, reduced spacing between buttons
  const headerHeightDesktop = 90;
  const columnLeftPositions = [
    { top: `${headerHeightDesktop + 50}px`, left: '128px' },  // Start below header (90px + 50px margin = 140px)
    { top: `${headerHeightDesktop + 130}px`, left: '128px' },  // 220px from top (80px spacing)
    { top: `${headerHeightDesktop + 210}px`, left: '128px' }, // 300px from top (80px spacing)
    { top: `${headerHeightDesktop + 290}px`, left: '128px' }  // 380px from top (80px spacing)
  ];

  // Hide entire overlay when hideHotspots is true
  if (hideHotspots) {
    return null;
  }

  // Mobile-responsive positions for buttons (header is ~80px tall on mobile)
  // Moved down to avoid overlap with Key Concepts container
  const headerHeightMobile = 80;
  const mobileColumnLeftPositions = [
    { top: `${headerHeightMobile + 60}px`, left: '12px' },  // Start below header (80px + 60px margin = 140px) - moved down 20px
    { top: `${headerHeightMobile + 120}px`, left: '12px' },  // 200px from top (60px spacing) - moved down 20px
    { top: `${headerHeightMobile + 180}px`, left: '12px' }, // 260px from top (60px spacing) - moved down 20px
    { top: `${headerHeightMobile + 240}px`, left: '12px' }   // 320px from top (60px spacing) - moved down 20px
  ];

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10000, overflow: 'visible' }}>
      {/* Header - Compact hint only (tabs removed) */}
      {showHeader && (
        <div className="absolute top-2 left-2 md:top-4 md:left-0 pointer-events-auto bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg border border-amber-200 w-auto max-w-[200px]">
          <p className="text-[10px] md:text-xs text-gray-600 leading-tight">Click a point to explore</p>
        </div>
      )}

      {/* Hotspot Buttons - Mobile responsive */}
      {!hideHotspots && points.map((text, idx) => {
        const s = typeof text === 'string' ? text : (text?.text || text?.appreciation || text?.title || '');
        // Use mobile positions on small screens, desktop positions on larger screens
        const mobilePos = mobileColumnLeftPositions[idx] || mobileColumnLeftPositions[mobileColumnLeftPositions.length - 1];
        const desktopPos = layout === 'columnLeft' ? (columnLeftPositions[idx] || columnLeftPositions[columnLeftPositions.length - 1]) : (DEFAULT_POSITIONS[idx] || { top: '50%', left: '50%' });
        const isActive = activeIndex === idx;
        
        return (
          <div
            key={`hotspot-${idx}`}
            className="absolute pointer-events-auto"
            style={{ 
              top: isMobile ? mobilePos.top : desktopPos.top, 
              left: isMobile ? mobilePos.left : desktopPos.left,
              transform: 'translateY(-50%)',
              zIndex: 10000
            }}
          >
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('[Hotspots] Hotspot clicked:', idx, 'text:', text);
                onHotspotClick(idx);
              }}
              className={`w-8 h-8 md:w-10 md:h-10 rounded-full text-white font-bold text-xs md:text-sm shadow-md border-2 border-amber-200 transition-all duration-200 ${
                isActive 
                  ? 'bg-amber-500 scale-110 shadow-xl' 
                  : 'bg-amber-400 hover:bg-amber-500 hover:scale-105'
              }`}
              style={{ 
                minWidth: '40px', 
                minHeight: '40px',
                zIndex: 10000
              }}
              title={
                idx === 0 ? 'Foreground Appreciation & Techniques' :
                idx === 1 ? 'Middle Ground Appreciation & Techniques' :
                idx === 2 ? 'Background Appreciation & Techniques' :
                'Composition Appreciation & Techniques'
              }
            >
              {idx + 1}
            </button>
          </div>
        );
      })}

      {/* Desktop: Right side - Show when point selected and not hidden */}
      {!hideHotspots && activeIndex !== null && (
        <div className="hidden md:block absolute top-0 right-0 w-80 h-full bg-white border-l-4 border-amber-200 shadow-xl p-4 overflow-y-auto pointer-events-auto">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h4 className="text-lg font-bold text-amber-700">
                Art Appreciation & Tech Insights - Point {activeIndex + 1}
              </h4>
              {/* Spatial Focus Labels */}
              <div className="mt-2">
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                  activeIndex === 0 ? 'bg-amber-100 text-amber-800' :
                  activeIndex === 1 ? 'bg-amber-100 text-amber-800' :
                  activeIndex === 2 ? 'bg-amber-100 text-amber-800' :
                  'bg-amber-100 text-amber-800'
                }`}>
                  {activeIndex === 0 ? 'FOREGROUND APPRECIATION & TECHNIQUES' :
                   activeIndex === 1 ? 'MIDDLE GROUND APPRECIATION & TECHNIQUES' :
                   activeIndex === 2 ? 'BACKGROUND APPRECIATION & TECHNIQUES' :
                   'COMPOSITION APPRECIATION & TECHNIQUES'}
                </span>
              </div>
            </div>
            <button
              onClick={() => onHotspotClick(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>
          </div>
          <div className="text-gray-800 leading-relaxed">
            {(() => {
              const t = points[activeIndex];
              return typeof t === 'string' ? t : (t?.text || t?.appreciation || t?.title || JSON.stringify(t));
            })()}
          </div>
        </div>
      )}
    </div>
  );
});

InteractiveOverlay.displayName = 'InteractiveOverlay';

// Add aggressive memo comparison to prevent unnecessary re-renders
const MemoizedInteractiveOverlay = memo(InteractiveOverlay, (prevProps, nextProps) => {
  // Only re-render if activeIndex, layout, points, showHeader, or hideHotspots actually changes
  return (
    prevProps.activeIndex === nextProps.activeIndex &&
    prevProps.layout === nextProps.layout &&
    prevProps.showHeader === nextProps.showHeader &&
    prevProps.hideHotspots === nextProps.hideHotspots &&
    prevProps.points.length === nextProps.points.length &&
    prevProps.points.every((point, index) => point === nextProps.points[index])
  );
});

MemoizedInteractiveOverlay.displayName = 'MemoizedInteractiveOverlay';

const DEFAULT_POSITIONS = [
  { top: '22%', left: '28%' },
  { top: '62%', left: '46%' },
  { top: '34%', left: '82%' },
  { top: '86%', left: '12%' }
];

// Flow layout: generate N positions vertically on the left side of the artwork
function getFlowHotspotPositions(count) {
  if (!count) return [];
  const left = 10; // % from left
  if (count === 1) return [{ left: `${left}%`, top: '50%' }];
  const positions = [];
  const topStart = 14;
  const topEnd = 86;
  for (let i = 0; i < count; i++) {
    const top = count === 1 ? 50 : topStart + (topEnd - topStart) * (i / (count - 1));
    positions.push({ left: `${left}%`, top: `${top}%` });
  }
  return positions;
}

function extractPointsFromAnalysis(analysisText) {
  console.log('[ExtractPoints] Input analysisText:', analysisText);
  
  if (!analysisText || typeof analysisText !== 'string') {
    console.log('[ExtractPoints] Invalid input, returning empty array');
    return [];
  }

  // Look for the specific spatial structure in the text
  // The analysis should have 4 distinct sections: foreground, middle ground, background, composition
  
  // Try to split by looking for spatial keywords
  const spatialKeywords = [
    'In the foreground',
    'The foreground',
    'In the middle ground', 
    'The middle ground',
    'In the background',
    'The background',
    'The overall composition',
    'The composition'
  ];
  
  let points = [];
  let currentIndex = 0;
  
  // Find each spatial section
  for (let i = 0; i < spatialKeywords.length; i += 2) {
    const keyword1 = spatialKeywords[i];
    const keyword2 = spatialKeywords[i + 1];
    
    const index1 = analysisText.indexOf(keyword1);
    const index2 = analysisText.indexOf(keyword2);
    
    if (index1 !== -1) {
      // Find the end of this section (next keyword or end of text)
      let endIndex = analysisText.length;
      for (let j = i + 2; j < spatialKeywords.length; j++) {
        const nextKeyword = spatialKeywords[j];
        const nextIndex = analysisText.indexOf(nextKeyword);
        if (nextIndex !== -1 && nextIndex > index1) {
          endIndex = nextIndex;
          break;
        }
      }
      
      const section = analysisText.substring(index1, endIndex).trim();
      if (section) {
        points.push(section);
        console.log(`[ExtractPoints] Found section ${i/2 + 1}:`, section.substring(0, 100) + '...');
      }
    }
  }
  
  console.log('[ExtractPoints] Found spatial sections:', points.length);
  
  // If we found 4 spatial sections, use them
  if (points.length === 4) {
    console.log('[ExtractPoints] Using 4 spatial sections');
    return points;
  }
  
  // Fallback: try to split by double newlines
  const paragraphs = analysisText
    .split(/\n\s*\n/)
    .map(p => p.trim())
    .filter(Boolean);

  console.log('[ExtractPoints] Paragraphs after double newline split:', paragraphs.length);

  // If we have 4 paragraphs, use them directly
  if (paragraphs.length === 4) {
    console.log('[ExtractPoints] Found 4 paragraphs, using directly');
    return paragraphs;
  }

  // Try single newline split
  const lines = analysisText
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean);

  console.log('[ExtractPoints] Lines after single newline split:', lines.length);

  // If we have 4 lines, use them directly
  if (lines.length === 4) {
    console.log('[ExtractPoints] Found 4 lines, using directly');
    return lines;
  }

  // Last resort: split into sentences and group into 4 points
  const sentences = analysisText
      .split(/(?<=[.!?])\s+/)
      .map(s => s.trim())
      .filter(Boolean);
  
  console.log('[ExtractPoints] Fallback to sentences:', sentences.length);
  
  // Group sentences into 4 points (roughly equal distribution)
  const sentencesPerPoint = Math.ceil(sentences.length / 4);
  points = [];
  for (let i = 0; i < 4; i++) {
    const start = i * sentencesPerPoint;
    const end = Math.min(start + sentencesPerPoint, sentences.length);
    if (start < sentences.length) {
      points.push(sentences.slice(start, end).join(' '));
    }
  }

  // Return exactly 4 points
  const result = points.slice(0, 4);
  console.log('[ExtractPoints] Final result length:', result.length);
  return result;
}

// Flow layout overlay: tabs + instruction above artwork, pins vertically on left (% positions), no side panels
const FlowLayoutOverlay = memo(({ points, activeIndex, onHotspotClick, hotspotPositions }) => {
  const n = points?.length || 0;
  const useCustom = hotspotPositions && hotspotPositions.length >= n;
  const rawPositions = useCustom ? hotspotPositions.slice(0, n) : getFlowHotspotPositions(n);
  const positions = rawPositions.map((p) => (p.x != null && p.y != null) ? { left: `${p.x}%`, top: `${p.y}%` } : p);
  return (
    <>
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          .artmosphere-hotspot-pulse {
            animation: artmosphere-pulse 2s ease-in-out infinite;
          }
        }
        @keyframes artmosphere-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.45); }
          50% { box-shadow: 0 0 0 10px rgba(251, 191, 36, 0); }
        }
      `}</style>
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10000 }}>
        {points.map((text, idx) => {
          const pos = positions[idx] || positions[0];
          const isActive = activeIndex === idx;
          return (
            <div
              key={`flow-hotspot-${idx}`}
              className="absolute pointer-events-auto"
              style={{
                left: pos.left,
                top: pos.top,
                transform: 'translate(-50%, -50%)',
                zIndex: 10001
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onHotspotClick && onHotspotClick(isActive ? null : idx);
                }}
                className={`artmosphere-hotspot-pulse rounded-full font-bold text-white shadow-md border-2 transition-all duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 ${
                  isActive ? 'bg-amber-500 border-amber-600 ring-2 ring-amber-400 ring-offset-2 scale-110' : 'bg-amber-400 border-amber-300 hover:bg-amber-500 hover:scale-105'
                }`}
                style={{ minWidth: 44, minHeight: 44, width: 44, height: 44 }}
                aria-label={typeof text === 'string' ? text : `Point ${idx + 1}`}
                title={typeof text === 'string' ? text : `Point ${idx + 1}`}
              >
                {idx + 1}
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
});
FlowLayoutOverlay.displayName = 'FlowLayoutOverlay';

// Main component with completely separate static artwork and interactive elements
const ArtAnalysisHotspots = memo(({ imageUrl, analysisText, title, enableFullscreen = true, layout = 'default', activity = null, showHeader = true, concepts = [], hideHotspots = false, hotspotPositions, activeIndex: controlledActiveIndex, onActivePointChange, activeSection: controlledActiveSection, onSectionChange }) => {
  if (isDev) console.log('[ArtAnalysisHotspots] render');
  const [internalActiveIndex, setInternalActiveIndex] = useState(null);
  const [internalActiveSection, setInternalActiveSection] = useState('appreciation');
  const activeIndex = layout === 'flow' && controlledActiveIndex !== undefined ? controlledActiveIndex : internalActiveIndex;
  const setActiveIndex = layout === 'flow' && onActivePointChange ? (v) => { onActivePointChange(v); } : setInternalActiveIndex;
  const activeSection = layout === 'flow' && controlledActiveSection !== undefined ? controlledActiveSection : internalActiveSection;
  const setActiveSection = layout === 'flow' && onSectionChange ? (s) => { onSectionChange(s); } : setInternalActiveSection;

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Preload image to prevent flashing; retry via proxy on 403
  useEffect(() => {
    if (!imageUrl) return;
    const done = () => setImageLoaded(true);
    const img = new Image();
    img.onload = () => { if (isDev) console.log('[ImagePreload] Image loaded'); done(); };
    img.onerror = () => {
      const current = img.src || imageUrl;
      if (current && !current.includes('/proxy-image?') && /^https?:\/\//i.test(current)) {
        img.onerror = () => { console.error('[ImagePreload] Image failed (including proxy):', imageUrl); done(); };
        img.onload = () => done();
        img.src = `${getBackendBaseUrl()}/proxy-image?url=${encodeURIComponent(current)}`;
      } else {
        console.error('[ImagePreload] Image failed to load:', imageUrl);
        done();
      }
    };
    img.src = resolveImageUrl(imageUrl);
  }, [imageUrl]);
  
  const points = useMemo(() => {
    if (isDev) console.log('[Hotspots] Computing points', Array.isArray(analysisText) ? 'array' : typeof analysisText);
    if (Array.isArray(analysisText)) {
      const normalize = (it) => (typeof it === 'string') ? it : (it?.text || it?.appreciation || it?.title || JSON.stringify(it));
      return analysisText.map(normalize);
    }
      
    const extractedPoints = extractPointsFromAnalysis(analysisText);
    if (extractedPoints.length === 0) {
      if (isDev) console.log('[Hotspots] No points extracted, using fallback');
      return [
          'Notice the vibrant use of complementary colors and color theory that draw the viewer\'s eye across the canvas, creating visual harmony.',
          'Observe how the artist employs chiaroscuro and tenebrism techniques, using dramatic lighting and shadows to give depth and dimension.',
          'Look at the composition following the rule of thirds and how elements are arranged to create perfect balance and focal points.',
          'Pay attention to the impasto brushwork and texture techniques, where thick paint application creates tactile surface qualities.'
      ];
    }
    
    return extractedPoints;
  }, [analysisText]);

  if (!imageUrl) return <div className="text-red-500">No image URL provided</div>;
  if (!imageLoaded) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading artwork...</p>
        </div>
      </div>
    );
  }
  
  if (points.length === 0) {
    console.log('[Hotspots] No points extracted, analysisText:', analysisText);
    return (
      <div className="relative">
        <img 
          src={resolveImageUrl(imageUrl)} 
          alt={title || 'Artwork'} 
          className="w-full h-full object-contain bg-white"
          onError={(e) => {
            const current = e.currentTarget.getAttribute('data-src') || e.currentTarget.src || imageUrl || '';
            if (current && !current.includes('/proxy-image?') && /^https?:\/\//i.test(current)) {
              e.currentTarget.src = `${getBackendBaseUrl()}/proxy-image?url=${encodeURIComponent(current)}`;
            }
          }}
          data-src={imageUrl}
        />
        <div className="absolute bottom-4 left-4 bg-red-100 text-red-600 p-2 rounded">
          No analysis points found. Text: {analysisText || 'Empty'}
        </div>
      </div>
    );
  }

  // Flow layout: artwork hero + overlay hotspots (no sidebar), right panel rendered by parent
  if (layout === 'flow') {
    return (
      <div className="w-full h-full flex flex-col" style={{ height: '100%', minHeight: '280px' }}>
        <div className="flex-1 min-h-0 relative w-full rounded-xl md:rounded-2xl overflow-hidden border-2 md:border-4 border-amber-200 shadow-lg bg-amber-50/80" style={{ contain: 'layout' }}>
          <StaticArtwork imageUrl={imageUrl} title={title} />
          <FlowLayoutOverlay
            points={points}
            activeIndex={activeIndex}
            onHotspotClick={(idx) => setActiveIndex(activeIndex === idx ? null : idx)}
            hotspotPositions={hotspotPositions}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col" style={{ height: '100%', minHeight: '400px' }} data-component-id={`art-analysis-${Date.now()}`}>
      {/* Artwork Container - Completely static, never changes */}
      <div className="flex-1 min-h-0 relative flex-shrink-0" style={{ minHeight: '400px', height: '100%' }}>
        <div className="w-full h-full rounded-xl md:rounded-2xl overflow-hidden border-2 md:border-4 border-amber-200 shadow-lg relative pl-0 md:pl-64" style={{ height: '100%' }}>
          {/* Static artwork that never re-renders */}
          <StaticArtwork imageUrl={imageUrl} title={title} />
          
          {/* Interactive overlay that handles all state changes */}
          <MemoizedInteractiveOverlay 
            key={`interactive-overlay-${imageUrl}`}
            points={points}
            activeIndex={activeIndex}
            onHotspotClick={(idx) => {
              if (isDev) console.log('[Hotspots] Button clicked:', idx);
              setActiveIndex(activeIndex === idx ? null : idx);
            }}
            layout={layout}
            showHeader={showHeader}
            hideHotspots={hideHotspots}
          />
        </div>
      </div>
      
      {/* Mobile: Details Panel Below Artwork */}
      {!hideHotspots && activeIndex !== null && points && points[activeIndex] && (
        <div className="md:hidden w-full bg-white border-t-4 border-amber-200 shadow-xl p-3 max-h-[40vh] overflow-y-auto pointer-events-auto rounded-b-xl flex-shrink-0 mt-2">
          {console.log('[Hotspots] Rendering mobile analysis panel for activeIndex:', activeIndex, 'Points:', points)}
          <div className="flex justify-between items-center mb-3">
            <div className="flex-1 min-w-0 pr-2">
              <h4 className="text-base font-bold text-amber-700">
                Point {activeIndex + 1}
              </h4>
              {/* Spatial Focus Labels */}
              <div className="mt-1">
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  activeIndex === 0 ? 'bg-amber-100 text-amber-800' :
                  activeIndex === 1 ? 'bg-amber-100 text-amber-800' :
                  activeIndex === 2 ? 'bg-amber-100 text-amber-800' :
                  'bg-amber-100 text-amber-800'
                }`}>
                  {activeIndex === 0 ? 'FOREGROUND' :
                   activeIndex === 1 ? 'MIDDLE GROUND' :
                   activeIndex === 2 ? 'BACKGROUND' :
                   'COMPOSITION'}
                </span>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveIndex(null);
              }}
              className="text-gray-500 hover:text-gray-700 flex-shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>
          </div>
          <div className="text-gray-800 leading-relaxed text-sm">
            {(() => {
              const t = points[activeIndex];
              return typeof t === 'string' ? t : (t?.text || t?.appreciation || t?.title || JSON.stringify(t));
            })()}
          </div>
        </div>
      )}

      {isFullscreen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative w-full max-w-5xl">
            <div className="absolute top-3 right-3 z-50 flex gap-2">
              <button
                onClick={() => setIsFullscreen(false)}
                className="bg-white/90 hover:bg-white text-gray-800 rounded-full px-3 py-1 shadow border"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="w-full h-full rounded-2xl overflow-hidden border-4 border-amber-200 shadow-lg relative">
              <StaticArtwork imageUrl={imageUrl} title={title} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  if (prevProps.layout !== nextProps.layout) return false;
  if (prevProps.imageUrl !== nextProps.imageUrl) return false;
  if (prevProps.layout === 'flow') {
    return prevProps.activeIndex === nextProps.activeIndex &&
      prevProps.onActivePointChange === nextProps.onActivePointChange;
  }
  return true;
});

ArtAnalysisHotspots.displayName = 'ArtAnalysisHotspots';

export default ArtAnalysisHotspots;